/**
 * WeaveIn.ai Platform - Model Performance Dashboard
 * Shows ML model accuracy, outcome distribution, and training status.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Brain,
  TrendingUp,
  Database,
  CheckCircle,
  AlertCircle,
  BarChart3,
} from 'lucide-react';
import { predictionsAPI, ModelPerformance } from '@/services/api/predictions';

export const ModelPerformanceDashboard: React.FC<{ className?: string }> = ({ className = '' }) => {
  const { data, isLoading } = useQuery({
    queryKey: ['model-performance'],
    queryFn: () => predictionsAPI.getModelPerformance(),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading || !data) {
    return (
      <div className={`bg-white border rounded-lg p-4 ${className}`}>
        <div className="flex items-center gap-2 text-gray-400 text-sm">
          <Brain className="w-4 h-4 animate-pulse" />
          Loading model metrics...
        </div>
      </div>
    );
  }

  const totalOutcomes = Object.values(data.outcome_distribution).reduce((a, b) => a + b, 0);

  return (
    <div className={`bg-white border rounded-lg ${className}`}>
      <div className="px-4 py-3 border-b flex items-center gap-2">
        <BarChart3 className="w-4 h-4 text-indigo-600" />
        <h3 className="text-sm font-semibold">ML Model Performance</h3>
      </div>

      <div className="p-4 space-y-4">
        {/* Accuracy card */}
        <div className="flex items-center gap-4">
          <div className="flex-shrink-0 w-16 h-16 rounded-full border-4 border-indigo-200 flex items-center justify-center">
            <span className="text-lg font-bold text-indigo-700">
              {data.recent_accuracy_30d !== null
                ? `${(data.recent_accuracy_30d * 100).toFixed(0)}%`
                : '—'
              }
            </span>
          </div>
          <div>
            <div className="text-sm font-medium text-gray-900">30-Day Accuracy</div>
            <div className="text-xs text-gray-500">
              Based on predictions vs actual outcomes
            </div>
          </div>
        </div>

        {/* Outcome distribution */}
        {totalOutcomes > 0 && (
          <div>
            <div className="text-xs font-medium text-gray-600 mb-2">Outcome Distribution</div>
            <div className="space-y-1.5">
              {Object.entries(data.outcome_distribution).map(([type, count]) => {
                const pct = (count / totalOutcomes) * 100;
                const colors: Record<string, string> = {
                  retained: 'bg-green-500',
                  churned: 'bg-red-500',
                  re_engaged: 'bg-blue-500',
                  dropped: 'bg-gray-400',
                };
                return (
                  <div key={type} className="flex items-center gap-2">
                    <span className="text-xs text-gray-600 w-20">{type}</span>
                    <div className="flex-1 h-2 bg-gray-100 rounded-full">
                      <div
                        className={`h-2 rounded-full ${colors[type] || 'bg-gray-300'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="text-xs text-gray-500 w-10 text-right">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Training status */}
        <div className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-md">
          <Database className="w-4 h-4 text-gray-400" />
          <div className="text-xs">
            <span className="font-medium text-gray-700">
              {data.unused_outcomes_for_training}
            </span>
            <span className="text-gray-500"> outcomes awaiting next training batch</span>
          </div>
          {data.unused_outcomes_for_training >= 100 && (
            <span className="ml-auto flex items-center gap-1 text-xs text-amber-600">
              <AlertCircle className="w-3 h-3" />
              Retrain recommended
            </span>
          )}
          {data.unused_outcomes_for_training < 100 && (
            <span className="ml-auto flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="w-3 h-3" />
              Model current
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default ModelPerformanceDashboard;
